// Central date formatting, driven by the user's region + date-format preference (Settings). Every page
// formats dates through useDateFormatter() (or useFormatDate() for a plain date), so one choice in
// Settings applies everywhere. The pure formatters live in lib/dates.ts for code outside React.

import { useCallback, useMemo } from 'react';
import { useSettings, type DateFmt } from '../settings/SettingsContext.js';
import { formatDate, makeDateFormatter, type DateFormatter, type DateInput } from './dates.js';

export { formatDate } from './dates.js';

/** hook: every date/time formatter bound to the current region + date format. Stable until they change. */
export function useDateFormatter(): DateFormatter {
  const { prefs } = useSettings();
  return useMemo(
    () => makeDateFormatter({ locale: prefs.region, dateFormat: prefs.dateFormat }),
    [prefs.region, prefs.dateFormat],
  );
}

/** hook: a plain date in the user's format (single argument, so it can go straight into .map). */
export function useFormatDate(): (input: DateInput) => string {
  const df = useDateFormatter();
  return useCallback((input: DateInput) => df.date(input), [df]);
}

/** a short human sample of a given format, for live previews in the Settings page. */
export function sampleDate(locale: string, dateFormat: DateFmt): string {
  return formatDate(new Date(2026, 2, 9), { locale, dateFormat });
}

/** Status value -> Pill/`.pill` colour class, so Yes / No / Hold read as they do on the Overview grid, in
 *  the one status vocabulary: Yes = ok green, Hold = warn amber (`hold`), No = neutral (''). No is not a
 *  state to flag, so it takes the plain pill; '' is also what any other value gets, so a caller that pills
 *  only the status values asks isStatusValue() rather than testing the kind. Case/space-insensitive. */
export function statusPillKind(v: string | null | undefined): string {
  switch (String(v ?? '').trim().toLowerCase()) {
    case 'yes': return 'ok';
    case 'hold': return 'hold';
    default: return '';
  }
}

/** whether a value is one of the Yes / No / Hold statuses that statusPillKind classes (No included). */
export function isStatusValue(v: string | null | undefined): boolean {
  const s = String(v ?? '').trim().toLowerCase();
  return s === 'yes' || s === 'no' || s === 'hold';
}
